import React, {useEffect, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {Autocomplete, Box, Button, GlobalStyles, Grid, MenuItem, TextField, Tooltip, Typography} from '@mui/material';
import {keyframes} from '@mui/material/styles';
import {
    getCpciCountriesService,
    getCpcGovUserInfoService,
    saveCpcGovUserInfoService,
    isCpcGovLoggedIn,
} from '../../../services/cpcGov.service';
import {toastSuccess} from '../../../utils/utils';
import GalanoRegular from '../../../assets/fonts/cpc/GalanoGrotesque-Regular.otf';
import GalanoBold from '../../../assets/fonts/cpc/GalanoGrotesque-Bold.otf';
import CpcHeader from '../components/CpcHeader';
import CpcFooter from '../components/CpcFooter';
import {COUNTRIES} from '../countries';

const STORAGE_KEY = 'cpcGovernmentUserInfo';

const REVIEW_ROLES = [
    'Lead reviewer',
    'Contributor',
    'Approver',
    'Technical expert',
    'Other',
];

const AUTHORIZED_OPTIONS = ['Yes', 'No'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Official telephone number is international and optional — loose on format,
// just reject anything that clearly isn't digits/punctuation (e.g. letters).
const PHONE_RE = /^\+?[0-9()\-\s]{7,20}$/;

/**
 * The form is edited as flat strings but stored by the API under its own field
 * names, so the two shapes are mapped explicitly rather than passed through.
 */
const toApiPayload = (form) => ({
    full_name: form.fullName,
    organization: form.org,
    job_title: form.job,
    official_email: form.email,
    official_phone: form.phone,
    // review_role: form.role,
    areas_of_responsibility: form.area,
    authorized_to_submit: form.authorized === '' ? null : form.authorized === 'Yes',
    additional_reviewers: parseReviewers(form.addlReviewers),
});

const fromApiProfile = (p) => ({
    fullName: p.full_name || '',
    org: p.organization || '',
    job: p.job_title || '',
    email: p.official_email || '',
    phone: p.official_phone || '',
    // role: p.review_role || '',
    area: p.areas_of_responsibility || '',
    authorized: p.authorized_to_submit === true ? 'Yes' : p.authorized_to_submit === false ? 'No' : '',
    addlReviewers: formatReviewers(p.additional_reviewers),
});

/**
 * "Additional reviewers" is a textarea on the mockup — one reviewer per line,
 * written as "name, role, email". The API stores them structured so they can be
 * reported on later, so the two are converted here rather than storing the blob.
 */
function parseReviewers(text) {
    return String(text || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const [name = '', role = '', email = ''] = line.split(',').map((s) => s.trim());
            return {name, role, email};
        });
}

function formatReviewers(rows) {
    if (!Array.isArray(rows)) return '';
    return rows
        .map((r) => [r.name, r.role, r.email].filter(Boolean).join(', '))
        .filter(Boolean)
        .join('\n');
}

const C = {
    // navy/navyDeep/white stay literal — see the matching comment in
    // NationalOverview.page.jsx. Plain text/background roles use the
    // dedicated keys below, which flip via --cpc-* (see darkMode.css).
    navy: '#003C69',
    navyDeep: '#003D69',
    navyDark: '#002855',
    cyan: '#0096C8',
    cyanDeep: '#007DB4',
    white: '#FFFFFF',
    mist: 'var(--cpc-surface-subtle, #F5F8FB)',
    muted: 'var(--cpc-text-muted, rgba(0,60,105,.72))',
    required: '#007DB4',
    disabled: '#c5d0da',
    error: '#C0553A', // RUST, same value the dashboard uses for "not met"
    surface: 'var(--cpc-surface, #FFFFFF)',
    text: 'var(--cpc-text, #003C69)',
};

const emptyForm = {
    country: '',
    fullName: '',
    org: '',
    job: '',
    email: '',
    password: '',
    phone: '',
    role: '',
    area: '',
    authorized: '',
    addlReviewers: '',
};

const fontFaceStyles = {
    '@font-face': [
        {fontFamily: 'GalanoGrotesque', fontWeight: 400, src: `url(${GalanoRegular}) format("opentype")`, fontDisplay: 'swap'},
        {fontFamily: 'GalanoGrotesque', fontWeight: 700, src: `url(${GalanoBold}) format("opentype")`, fontDisplay: 'swap'},
    ],
};

const cpcRise = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: none; }
`;

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        background: C.mist,
        fontSize: 15,
        fontFamily: 'inherit',
        color: C.navyDeep,
        '& fieldset': {borderColor: 'rgba(0,60,105,.2)'},
        '&:hover fieldset': {borderColor: 'rgba(0,60,105,.2)'},
        '&.Mui-focused': {background: C.white},
        '&.Mui-focused fieldset': {borderColor: C.cyanDeep, borderWidth: 1, boxShadow: '0 0 0 3px rgba(0,150,200,.15)'},
    },
    '& .MuiOutlinedInput-input::placeholder': {color: 'var(--cpc-text-muted, rgba(0,60,105,.45))', opacity: 1},
};

const FieldLabel = ({children, required}) => (
    <Typography component="span" sx={{fontSize: 15, fontWeight: 700, color: C.text, mb: 0.75, display: 'block'}}>
        {children} {required && <Box component="span" sx={{color: C.required}}>*</Box>}
    </Typography>
);

const UserInfoPage = () => {
    const history = useHistory();
    const [form, setForm] = useState(emptyForm);
    // Snapshot of the form as it was when loaded from the API / sessionStorage.
    // Used to detect whether the user has actually changed anything so we can
    // enable / disable the Save button accordingly.
    const [initialForm, setInitialForm] = useState(emptyForm);
    // The 100 CPCI countries, from the CPC-owned country master. Free text was
    // rejected in review: the name must be the UN-provided one, so it is chosen
    // from a fixed searchable list rather than typed.
    //
    // NOTE (approved flow, slide 18): in PRODUCTION this field becomes read-only
    // and is populated from the account's fixed country_code. The picker below is
    // for development and provisioning, until Gov accounts exist.
    const [countries, setCountries] = useState([]);
    useEffect(() => {
        let alive = true;
        getCpciCountriesService()
            .then((res) => {
                const list = res?.data?.countries;
                if (alive && Array.isArray(list)) setCountries(list);
            })
            .catch(() => { /* best effort — the field stays usable, just unlisted */ });
        return () => { alive = false; };
    }, []);

    // Signed in as a government reviewer? Then the profile comes from (and goes
    // back to) the API, and the country is whatever the account is fixed to.
    // Signed out, the page keeps working against sessionStorage so the screen is
    // still reviewable before accounts are provisioned.
    const authed = isCpcGovLoggedIn();
    const [lockedCountryCode, setLockedCountryCode] = useState('');
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [showValidation, setShowValidation] = useState(false);

    const lockedCountryName = useMemo(() => {
        if (!lockedCountryCode) return '';
        const hit = countries.find((c) => c.country_code === lockedCountryCode);
        // Fall back to the code itself if the master has not loaded yet — better
        // than showing an empty field on the account's own country.
        return hit ? hit.country_name : lockedCountryCode;
    }, [lockedCountryCode, countries]);

    /**
     * Options for the signed-out country picker, as plain names.
     *
     * Prefers the LIVE list from /meta/countries — that reads CPC's own
     * `cpci_country_list`, so the spelling is authoritative (UN M49, including
     * the parenthetical articles like "Korea (the Republic of)") and can't drift
     * from the database the dashboard is scoped against. Falls back to the
     * bundled COUNTRIES list when the API is unreachable, so the screen stays
     * usable offline and before the endpoint is deployed.
     */
    const countryOptions = useMemo(
        () => (countries.length ? countries.map((c) => c.country_name) : COUNTRIES),
        [countries],
    );

    const emailError = !!form.email.trim() && !EMAIL_RE.test(form.email.trim());
    // Phone is optional — only flag it once something has actually been typed.
    const phoneError = !!form.phone.trim() && !PHONE_RE.test(form.phone.trim());

    const valid = useMemo(
        () => !!(
            (authed ? true : form.country.trim())
            && form.fullName.trim()
            && form.email.trim()
            && !emailError
            && !phoneError
        ),
        [form, authed, emailError, phoneError],
    );

    // True only when the user has actually changed at least one field vs. what
    // was loaded — prevents saving a form that is identical to the current state.
    const hasChanges = useMemo(
        () => Object.keys(form).some((k) => form[k] !== initialForm[k]),
        [form, initialForm]
    );

    const goBackToDashboard = () => {
        history.push('/dq_pro/cpc-government-dashboard/overview/executive-summary');
    };

    useEffect(() => {
        document.title = 'User Information';

        if (!authed) {
            try {
                const raw = sessionStorage.getItem(STORAGE_KEY);
                if (raw) {
                    const loaded = {...emptyForm, ...JSON.parse(raw)};
                    setForm(loaded);
                    setInitialForm(loaded);
                }
            } catch (_) {
                /* ignore */
            }
            return undefined;
        }

        let alive = true;
        getCpcGovUserInfoService()
            .then((res) => {
                if (!alive) return;
                const data = res?.data || {};
                setLockedCountryCode(data.country_code || '');
                if (data.profile) {
                    const loaded = fromApiProfile(data.profile);
                    setForm((prev) => ({...prev, ...loaded}));
                    setInitialForm((prev) => ({...prev, ...loaded}));
                }
            })
            .catch(() => { /* the form stays editable; saving will surface any error */ });
        return () => { alive = false; };
    }, [authed]);

    // Keep the displayed country in step with the account once the master loads.
    useEffect(() => {
        if (authed && lockedCountryName) {
            setForm((prev) => (prev.country === lockedCountryName ? prev : {...prev, country: lockedCountryName}));
            setInitialForm((prev) => (prev.country === lockedCountryName ? prev : {...prev, country: lockedCountryName}));
        }
    }, [authed, lockedCountryName]);

    const setField = (key) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({...prev, [key]: value}));
        setSaved(false);
        setSaveError('');
    };

    const setCountry = (_e, value) => {
        setForm((prev) => ({...prev, country: value || ''}));
        setSaved(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!valid) {
            setShowValidation(true);
            return;
        }

        if (!authed) {
            // Not provisioned yet — keep the screen reviewable end to end.
            try {
                // Never persist the password in client-side storage — only the
                // rest of the attribution details need to survive a page reload.
                const {password: _password, ...persisted} = form;
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
            } catch (_) {
                /* ignore */
            }
            setInitialForm(form);
            setSaved(true);
            toastSuccess('Your changes have been saved.');
            return;
        }

        try {
            // No country in the payload on purpose — the server writes it from
            // the session and ignores anything the client sends.
            await saveCpcGovUserInfoService(toApiPayload(form));
            setInitialForm(form);
            setSaved(true);
            setSaveError('');
            toastSuccess('Your changes have been saved.');
        } catch (err) {
            setSaveError(err?.message || 'Could not save your information. Please try again.');
        }
    };

    return (
        <Box
            className="cpc-gov-scope"
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'GalanoGrotesque, "Helvetica Neue", Helvetica, Arial, sans-serif',
                color: C.text,
                background: C.mist,
                WebkitFontSmoothing: 'antialiased',
            }}
        >
            <GlobalStyles styles={fontFaceStyles}/>

            <CpcHeader/>

            <Box
                component="main"
                sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '56px 28px 80px',
                    animation: `${cpcRise} .45s ease both`,
                    '@media (max-width: 720px)': {padding: '36px 18px 64px'},
                }}
            >
                <Box sx={{width: '100%', maxWidth: 840}}>
                    <Typography component="h1" sx={{margin: '0 0 12px', fontSize: 'clamp(26px, 3.5vw, 36px)', lineHeight: 1.15, fontWeight: 700, color: C.text}}>
                        User Information
                    </Typography>
                    <Typography sx={{margin: '0 0 28px', maxWidth: 740, fontSize: 17, lineHeight: 1.5, fontWeight: 600, color: C.text}}>
                        Please provide the information below so that submissions can be attributed, reviewed, and followed up through the official validation process.
                    </Typography>

                    <Box
                        component="form"
                        onSubmit={submit}
                        noValidate
                        sx={{background: C.surface, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))', padding: '32px 30px', boxShadow: '0 8px 28px rgba(0,40,85,.06)', '@media (max-width: 720px)': {padding: '24px 18px'}}}
                    >
                        <Typography sx={{fontSize: 14, color: C.muted, mb: 2.5}}>
                            Fields marked <Box component="span" sx={{color: C.required}}>*</Box> are required.
                        </Typography>

                        <Grid container spacing={2.25}>
                            <Grid item xs={12} sm={6}>
                                <FieldLabel required>Country</FieldLabel>
                                {authed ? (
                                    // Read-only: country_code is fixed on the account and is the
                                    // security scope for every request. It is shown, never chosen.
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={lockedCountryName}
                                        disabled
                                        helperText="Set by your account and cannot be changed."
                                        sx={fieldSx}
                                    />
                                ) : (
                                    <Autocomplete
                                        fullWidth
                                        options={countryOptions}
                                        value={form.country || null}
                                        onChange={setCountry}
                                        autoHighlight
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                placeholder="Search for a country…"
                                                error={showValidation && !form.country.trim()}
                                                helperText={showValidation && !form.country.trim() ? 'Country is required.' : undefined}
                                                sx={fieldSx}
                                            />
                                        )}
                                    />
                                )}
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FieldLabel required>Full name</FieldLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={form.fullName}
                                    onChange={setField('fullName')}
                                    placeholder="First and last name"
                                    error={showValidation && !form.fullName.trim()}
                                    helperText={showValidation && !form.fullName.trim() ? 'Full name is required.' : undefined}
                                    sx={fieldSx}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FieldLabel>Ministry, agency, or organization</FieldLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={form.org}
                                    onChange={setField('org')}
                                    placeholder="Official body you represent"
                                    sx={fieldSx}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FieldLabel>Job title and role</FieldLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={form.job}
                                    onChange={setField('job')}
                                    placeholder="e.g. Policy Adviser, Online Safety"
                                    sx={fieldSx}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FieldLabel required>Official email address</FieldLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    type="email"
                                    value={form.email}
                                    onChange={setField('email')}
                                    placeholder="name@gov.example"
                                    error={emailError || (showValidation && !form.email.trim())}
                                    helperText={emailError ? 'Enter a valid email address.' : (showValidation && !form.email.trim() ? 'Official email address is required.' : undefined)}
                                    sx={fieldSx}
                                />
                            </Grid>

                            {/* Password — hidden. This form does not maintain
                                credentials: a signed-in reviewer's account was
                                pre-created for their country, and setting the
                                real password belongs to the single-use
                                invitation link (slide 18, Phase 3 step 9), not
                                to this screen. Left commented rather than
                                deleted in case that changes.
                            <Grid item xs={12} sm={6}>
                                <FieldLabel>Password</FieldLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    type="password"
                                    autoComplete="new-password"
                                    value={form.password}
                                    onChange={setField('password')}
                                    placeholder="Choose a password"
                                    sx={fieldSx}
                                />
                            </Grid>
                            */}

                            <Grid item xs={12} sm={6}>
                                <FieldLabel>Official telephone number</FieldLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    type="tel"
                                    value={form.phone}
                                    onChange={setField('phone')}
                                    placeholder="+00 000 000 0000"
                                    error={phoneError}
                                    helperText={phoneError ? 'Enter a valid phone number.' : undefined}
                                    sx={fieldSx}
                                />
                            </Grid>

                            {/*<Grid item xs={12} sm={6}>*/}
                            {/*    <FieldLabel>Review role</FieldLabel>*/}
                            {/*    <TextField*/}
                            {/*        select*/}
                            {/*        fullWidth*/}
                            {/*        variant="outlined"*/}
                            {/*        size="small"*/}
                            {/*        value={form.role}*/}
                            {/*        onChange={setField('role')}*/}
                            {/*        sx={fieldSx}*/}
                            {/*    >*/}
                            {/*        <MenuItem value="">Select…</MenuItem>*/}
                            {/*        {REVIEW_ROLES.map((role) => (*/}
                            {/*            <MenuItem key={role} value={role}>{role}</MenuItem>*/}
                            {/*        ))}*/}
                            {/*    </TextField>*/}
                            {/*</Grid>*/}

                            <Grid item xs={12}>
                                <FieldLabel>Area(s) of responsibility</FieldLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={form.area}
                                    onChange={setField('area')}
                                    placeholder="e.g. child online safety, ICT policy"
                                    sx={fieldSx}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FieldLabel>Are you authorized to submit evidence and propose revisions on behalf of your organization?</FieldLabel>
                                <TextField
                                    select
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={form.authorized}
                                    onChange={setField('authorized')}
                                    sx={fieldSx}
                                >
                                    <MenuItem value="">Select…</MenuItem>
                                    {AUTHORIZED_OPTIONS.map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <FieldLabel>Additional reviewers from your organization (name, role, email)</FieldLabel>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    variant="outlined"
                                    size="small"
                                    value={form.addlReviewers}
                                    onChange={setField('addlReviewers')}
                                    placeholder="List any additional reviewers, one per line"
                                    sx={fieldSx}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{mt: 3}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap'}}>
                                <Tooltip title={hasChanges ? 'Save your changes to this profile' : 'No changes to save yet'} arrow>
                                    <span>
                                        <Button
                                            type="submit"
                                            disabled={valid && !hasChanges}
                                            sx={{
                                                border: '2px solid',
                                                borderColor: !(valid && !hasChanges) ? C.navy : C.disabled,
                                                background: !(valid && !hasChanges) ? C.navy : C.disabled,
                                                color: C.white,
                                                fontFamily: 'inherit',
                                                fontSize: 16,
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                padding: '14px 26px',
                                                borderRadius: 0,
                                                textTransform: 'none',
                                                boxShadow: !(valid && !hasChanges) ? '0 4px 6px rgba(0,0,0,.1)' : 'none',
                                                transition: 'background .2s ease, border-color .2s ease',
                                                '&:hover': {
                                                    background: !(valid && !hasChanges) ? C.navyDark : C.disabled,
                                                    borderColor: !(valid && !hasChanges) ? C.navyDark : C.disabled,
                                                },
                                                '&.Mui-disabled': {
                                                    color: C.white,
                                                    background: C.disabled,
                                                    borderColor: C.disabled,
                                                },
                                            }}
                                        >
                                            Save changes
                                        </Button>
                                    </span>
                                </Tooltip>

                                {authed && (
                                    <Tooltip title="Return to the CPC Government Dashboard" arrow>
                                        <Button
                                            type="button"
                                            onClick={goBackToDashboard}
                                            sx={{
                                                border: '2px solid var(--cpc-border, rgba(0,60,105,.25))',
                                                background: 'transparent',
                                                color: C.text,
                                                fontFamily: 'inherit',
                                                fontSize: 16,
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                padding: '14px 26px',
                                                borderRadius: 0,
                                                textTransform: 'none',
                                                transition: 'background .2s ease, border-color .2s ease',
                                                '&:hover': {
                                                    background: 'var(--cpc-border, rgba(0,60,105,.05))',
                                                    borderColor: C.text,
                                                },
                                            }}
                                        >
                                            Enter the Government Toolkit →
                                        </Button>
                                    </Tooltip>
                                )}

                            </Box>

                            {saved && (
                                <Typography sx={{mt: 1.5, fontSize: 14, fontWeight: 700, color: '#03AF9A'}}>
                                    ✓ User information saved successfully.
                                </Typography>
                            )}
                            {saveError && (
                                <Typography sx={{mt: 1.5, fontSize: 14, fontWeight: 700, color: C.error}}>
                                    {saveError}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            <CpcFooter/>
        </Box>
    );
};

export default UserInfoPage;
